import React, { useEffect, useState, useRef, forwardRef } from 'react';
import { i18n } from 'fishx';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import groupBy from 'lodash/groupBy';
import uniqueId from 'lodash/uniqueId';
import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import { Button, Input, Table, Icon, message } from '@whalecloud/fdx';
import CommonModal from '@/components/CommonModal';
import styles from './index.module.less';

const RefInput = forwardRef((props, ref) => (
  <Input ref={ref} {...props} />
));

let fellowNbrTypeList = [];

const FMCMember = props => {
  const refInput = useRef();
  const {
    placeholder = i18n('COMMON_SELECT_ICON'),
    form,
    value,
    onChange,
    memberLimit, // 最大值
    modelKey,
    ...preProps
  } = props;
  const dispatch = useDispatch();
  const loading = useSelector(state => (state.loading.effects['business/qryFellowNbrType4FMCBsnl']));
  const subsDetail = useSelector(state => (state[modelKey].subsDetail));
  const [visible, setVisible] = useState(false); // 弹框显示
  const [fmcGroupTypeList, setFMCGroupTypeList] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [currEditingObj, setEidtingObj] = useState({ fellowNbrTypeId: null, memLimit: null, scriptRule: null });
  const [currEditingNumberType, setEditingNumberType] = useState(null);

  useEffect(() => {
    if (!visible) {
      setEditingKey(null);
      setDataSource([]);
      refInput.current.blur();
    } else {
      dispatch({
        type: 'business/qryFellowNbrType4FMCBsnl',
      }).then(res => {
        fellowNbrTypeList = res?.map(item => ({ ...item, label: item.fellowNbrTypeName, value: item.fellowNbrTypeId }));
        setFMCGroupTypeList(fellowNbrTypeList);
        if (!isEmpty(value)) {
          setDataSource(value);
        }
      });
    }
  }, [visible]);

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick, false);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  const handleDocumentClick = (event) => {
    const { target } = event;
    const className = `${get(target, 'className') || ''}`;
    if (className.indexOf('portal-select-item  portal-select-item-option') >= 0 || className === 'portal-select-item-option-content') {
      const node = className.indexOf('portal-select-item  portal-select-item-option') >= 0 ? target : target.parentNode;
      const reactHandlerKey = Object.keys(node).filter(item => item.indexOf('__reactEventHandlers') >= 0);
      const reactHandler = node[reactHandlerKey[0]];
      if (reactHandler.fellowNbrTypeId) {
        const { fellowNbrTypeId, memLimit, scriptRule } = fellowNbrTypeList.find(item => item.fellowNbrTypeId === reactHandler.fellowNbrTypeId) || {};
        setEidtingObj({ fellowNbrTypeId, memLimit, scriptRule, editable: true });
      }
      if (reactHandler.numberType) {
        setEditingNumberType(reactHandler.numberType);
      }
    }
  };

  const handleSubmit = () => {
    if (dataSource.length <= 0) {
      message.info(i18n('PLEASE_ADD_AN_FMC_MEMBER'));
      return;
    }
    onChange(dataSource);
    setVisible(false);
  };

  const getNumberTypeList = eidting => {
    // 如果STD_CODE=INTRA，可选值为 GSM、Fixed Voice；如果STD_CODE=INTER，可选值为 Fixed Voice
    if (get(fellowNbrTypeList.find(item => item.fellowNbrTypeId === eidting.fellowNbrTypeId), 'stdCode') === 'INTER') {
      return (
        [{
          label: i18n('FMC_FIXED_VOICE'),
          value: 'FIXED',
          numberType: 'FIXED',
        }]
      );
    }
    return (
      [{
        label: i18n('FMC_FIXED_VOICE'),
        value: 'FIXED',
        numberType: 'FIXED',
      }, {
        label: i18n('FMC_GROUP_GSM'),
        value: 'GSM',
        numberType: 'GSM',
      }]
    );
  };

  const fellowNbrEditRules = () => {
    const regExpArr = [];
    regExpArr.push({
      validator: async (rule, fellowNbr, callback) => {
        if (fellowNbr) {
          if (!isEmpty(dataSource)) {
            const someDataSource = dataSource.filter(item => item.fellowNbr === fellowNbr);
            if (someDataSource.length >= 1) {
              callback(i18n('SAME_NUMBER_ENTERED'));
            }
          }
          if (currEditingNumberType === 'FIXED') {
            const rtn = await dispatch({
              type: 'business/fmcFixedNumberCheck',
              payload: {
                fmcGroupType: currEditingObj.fellowNbrTypeId,
                fixedNumber: fellowNbr,
              },
              modelKey,
            });
            if (get(rtn, 'operationFlag') !== 'Y') {
              callback(get(rtn, 'returnMsg'));
            }
          } else if (currEditingNumberType === 'GSM') {
            // 关于联系人号码的校验，参考2692237
            if (fellowNbr.length !== 11) {
              callback(i18n('MOBILE_NUMBER_LENGTH'));
            } else if (!fellowNbr.startsWith('0')) {
              callback(i18n('MOBILE_NUMBER_STARTS_WITH_ZERO'));
            } else if (/([0-9])\1{9}/.test(fellowNbr.substring(1))) { // 如果去除开头的0之后，剩下的数字全都是一样的，则给出提示
              callback(i18n('MOBILE_NUMBER_SHOULD_NOT_BE_SAME'));
            } else if (!['06', '07', '08', '09'].some(v => fellowNbr.startsWith(v))) {
              callback(i18n('MOBILE_NUMBER_START_WITH'));
            }
          }
        }
        callback();
      },
    });
    return [{
      required: true, message: i18n('COMMON_PLEASE_INPUT'),
    }, ...regExpArr];
  };

  const columns = [
    {
      title: i18n('FMC_GROUP_SCHEME'),
      dataIndex: 'fellowNbrTypeId',
      width: 190,
      editable: currEditingObj.editable,
      editType: 'select',
      editOptions: fmcGroupTypeList,
      render: text => (fellowNbrTypeList.find(item => `${item.value}` === `${text}`)?.label),
      editRules: [{ required: true, message: i18n('COMMON_PLEASE_SELECT') }],
    },
    {
      title: i18n('FMC_NUMBER_TYPE'),
      dataIndex: 'ext2',
      width: 158,
      editable: true,
      editType: 'select',
      editOptions: getNumberTypeList(currEditingObj),
      render: text => (getNumberTypeList(currEditingObj).find(item => `${item.value}` === `${text}`)?.label),
      editRules: [{ required: true, message: i18n('COMMON_PLEASE_SELECT') }],
    },
    {
      title: i18n('FMC_PHONE_NUMBER'),
      dataIndex: 'fellowNbr',
      width: 161,
      editable: true,
      editRules: fellowNbrEditRules(),
    },
    {
      title: i18n('TABLE_OPERATION'),
      dataIndex: 'edit',
      width: 161,
      editable: true,
      render: (text, record) => <a onClick={() => handleDel(record.key)}><Icon type="delete" /></a>,
      editContent: () => <Icon type="edit" />,
      saveContent: () => <Icon type="save" />,
      cancelContent: () => <Icon type="stop" />,
    },
  ];

  const handleDel = key => {
    const newData = dataSource.filter(item => item.key !== key);
    setDataSource(newData);
    if (newData.length === 0 || editingKey === key) {
      setEditingKey(null);
    }
  };

  const handleAdd = () => {
    const uuidKey = uniqueId();
    let defaultFellowNbrTypeId = null;
    let editable = true;
    let newFMCGroupTypeList = cloneDeep(fmcGroupTypeList);
    if (dataSource.length > 0) {
      const groupByFellowNbrTypeIdObj = groupBy(dataSource, item => item.fellowNbrTypeId);
      forEach(groupByFellowNbrTypeIdObj,
        (item, key) => {
          const keyMemLimit = get(fellowNbrTypeList.find(it => `${it.fellowNbrTypeId}` === `${key}`), 'memLimit') || 99999;
          if (item.length === keyMemLimit) {
            newFMCGroupTypeList = fmcGroupTypeList.filter(it => `${it.fellowNbrTypeId}` !== `${key}`);
            setFMCGroupTypeList(newFMCGroupTypeList);
          }
          if (item.length < keyMemLimit) {
            defaultFellowNbrTypeId = +key;
            editable = false;
          }
        }
      );
    }
    if (!defaultFellowNbrTypeId) {
      defaultFellowNbrTypeId = newFMCGroupTypeList[0]?.value;
    }
    const defaultFellowNbrType = fmcGroupTypeList.find(item => item.fellowNbrTypeId === defaultFellowNbrTypeId);
    setEidtingObj({
      fellowNbrTypeId: defaultFellowNbrType?.fellowNbrTypeId,
      memLimit: defaultFellowNbrType?.memLimit,
      scriptRule: defaultFellowNbrType?.scriptRule,
      editable,
    });
    setEditingNumberType(getNumberTypeList(defaultFellowNbrType)[0]?.value);
    const data = dataSource.concat({
      key: uuidKey,
      fellowNbrTypeId: defaultFellowNbrType?.value,
      ext1: dataSource.length,
      ext2: getNumberTypeList(defaultFellowNbrType)[0]?.value,
      fellowNbr: '',
      subsId: get(subsDetail, 'subsId'),
    });
    setDataSource(data);
    setEditingKey(uuidKey);
  };

  const onSaveRowEdit = (record, newData) => {
    setEditingKey(null);
    setDataSource(newData);
  };

  const onCancelRowEdit = record => {
    setEditingKey(null);
    if (!record.fellowNbr) {
      // 如果没有输入号码，删掉此行
      setTimeout(() => handleDel(record.key), 200);
    }
  };

  const onRowEditing = record => {
    setEditingKey(record.key);
    let editable = true;
    const editingFellowNbrType = fellowNbrTypeList.find(item => item.fellowNbrTypeId === record.fellowNbrTypeId);
    if (dataSource.length > 0) {
      const groupByFellowNbrTypeIdObj = groupBy(dataSource, item => item.fellowNbrTypeId);
      const editingFellowNbrTypeList = groupByFellowNbrTypeIdObj[editingFellowNbrType.fellowNbrTypeId];
      if (editingFellowNbrTypeList <= editingFellowNbrType.memLimit) {
        editable = false;
      }
    }
    setEidtingObj({
      fellowNbrTypeId: editingFellowNbrType.fellowNbrTypeId,
      memLimit: editingFellowNbrType.memLimit,
      scriptRule: editingFellowNbrType.scriptRule,
      editable,
    });
    setEditingNumberType(getNumberTypeList(editingFellowNbrType.fellowNbrTypeId)[0]?.value);
  };

  const getValue = () => {
    const arr = [];
    // eslint-disable-next-line no-unused-expressions
    value?.forEach(element => {
      if (element.fellowNbr) {
        arr.push(element.fellowNbr);
      }
    });
    return arr.join(',');
  };

  const handleDrag = data => {
    const newData = data.map((item, index) => ({ ...item, ext1: index }));
    setDataSource(newData);
  };

  return (
    <>
      <RefInput
        ref={refInput}
        value={getValue()}
        placeholder={placeholder}
        autoComplete="off"
        onClick={() => setVisible(true)}
        suffix={<span className="iconfont2 iconsend" style={{ color: '#ABACB2' }} onClick={() => setVisible(true)} />}
        {...preProps}
      />
      {visible && (
        <CommonModal
          modalOpts={{
            title: i18n('FMC_MEMBER'),
            footer: [
              <Button onClick={() => setVisible(false)} key="COMMON_CANCEL">{i18n('COMMON_CANCEL')}</Button>,
              <Button type="primary" onClick={handleSubmit} key="COMMON_SUBMIT" disabled={editingKey}>{i18n('COMMON_SUBMIT')}</Button>,
            ],
            draggable: false,
            onCancel: () => setVisible(false),
            width: 960,
            className: `${styles.fmcMemberModal}`,
          }}
        >
          <div>
            <Button style={{ marginBottom: 14 }} type="primary" onClick={handleAdd} disabled={editingKey || dataSource.length === +memberLimit}>
              <span className="iconfont2 iconplus" />
              {editingKey ? `${i18n('COMMON_EDITING')}...` : i18n('BUTTON_ADD')}
            </Button>
            {
              dataSource.length > 0 && +memberLimit &&
              (
                <span style={{ marginLeft: 15, color: 'red' }}>max: {memberLimit}</span>
              )
            }
          </div>
          <Table
            style={{ marginBottom: 83 }}
            loading={loading}
            columns={columns}
            dataSource={dataSource}
            editRow
            editingKey={editingKey}
            onRowEditing={onRowEditing}
            onSaveRowEdit={onSaveRowEdit}
            onCancelRowEdit={onCancelRowEdit}
            dragable
            afterRowDrag={handleDrag}
          />
        </CommonModal>
      )}
    </>
  );
};

export default FMCMember;
